import { t } from "i18next";
import { formatDateDDMMYY } from "../../../utils/dateFormatter";
import {
  IskoriscenaAM,
  KategorijaEvidentiraneMarkice,
} from "./usedStamps.types";

const formatExcelCellValue = (val: unknown) => {
  if (val === null || val === undefined) return "";
  return String(val);
};

const formatOpseziUPrometuExcel = (
  info: IskoriscenaAM["opseziUPrometuInfo"]
) => {
  if (!info) return "";
  const opseziText = info.opsezi
    .reduce<string[]>((acc, opseg) => {
      const parts = [
        opseg.opseg,
        opseg.kolicina,
        opseg.ambalaza ? `(${opseg.ambalaza})` : null,
        opseg.datumStavljanjaUPromet,
      ].filter(Boolean);
      return [...acc, parts.join(" - ")];
    }, [])
    .join("; ");
  return `${info.kolicina}${opseziText ? `: ${opseziText}` : ""}`;
};

const getObligeeEvidentiraneMarkiceTableColumnDefs = (
  kategorijaEvidentiranihMarkicaId: KategorijaEvidentiraneMarkice
) => {
  const colDefs = [
    {
      title: t("EVIDENCIJA_MARKICA.BROJ_ZAM_ZAHTEVA"),
      dataIndex: "brojZahteva",
      isVisible: true,
    },
    {
      title: t("EVIDENCIJA_MARKICA.OZNAKA_VRSTE_MARKICE"),
      dataIndex: "markicaKategorijeVrste",
      isVisible: true,
    },
    {
      title: t("AKCIZNE_MARKICE.PERIOD"),
      dataIndex: "period",
      isVisible: [
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.OPSEG_SERIJSKIH_BROJEVA"),
      dataIndex: "opsegEvidentiranihMarkica",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.KOLICINA"),
      dataIndex: "kolicina",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: `${t("EVIDENCIJA_MARKICA.ZAPREMINA_AMBALAZE")} (L) /
                  ${t("EVIDENCIJA_MARKICA.KOLICINA")} (kg) ${t(
        "EVIDENCIJA_MARKICA.PO_POJEDINACNOM_PAKOVAJNU"
      )}`,
      dataIndex: "ambalaza",
      isVisible: [KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ].includes(
        kategorijaEvidentiranihMarkicaId
      ),
    },
    {
      title: t("EVIDENCIJA_MARKICA.KOLICINA"),
      dataIndex: "kolicinaOstecenihVidljivaSerija",
      isVisible: [KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE].includes(
        kategorijaEvidentiranihMarkicaId
      ),
    },
    {
      title: t("EVIDENCIJA_MARKICA.DATUM_EVIDENTIRANJA"),
      dataIndex: "datumEvidentiranja",
      isVisible: [
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.DATUM_PROIZVODNJE_ODNOSNO_CARINJENJA"),
      dataIndex: "opsegDatumaProizvodnje",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.LOKACIJA"),
      dataIndex: "lokacija",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.MASINA"),
      dataIndex: "masina",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.BREND"),
      dataIndex: "brend",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.STAVLJENO_U_PROMET_PRODAJU"),
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
      ].includes(kategorijaEvidentiranihMarkicaId),
      render: () => null,
    },
  ];

  return colDefs.filter((col) => col.isVisible);
};

export const getObligeeEvidentiraneMarkiceExcelColumns = (
  kategorijaEvidentiranihMarkicaId: KategorijaEvidentiraneMarkice
) => {
  const tableColumns = getObligeeEvidentiraneMarkiceTableColumnDefs(
    kategorijaEvidentiranihMarkicaId
  );

  return tableColumns.reduce<
    Array<{
      title: string;
      dataIndex: string;
      excelWidth?: number;
      excelValueFormatter?: (
        val: unknown,
        record?: IskoriscenaAM
      ) => string;
    }>
  >((acc, col) => {
    if (!col.dataIndex) {
      if (col.render) {
        return [
          ...acc,
          {
            title: t("EVIDENCIJA_MARKICA.STAVLJENO_U_PROMET_PRODAJU"),
            dataIndex: "opseziUPrometuInfo",
            excelWidth: 40,
            excelValueFormatter: (val) =>
              formatOpseziUPrometuExcel(
                val as IskoriscenaAM["opseziUPrometuInfo"]
              ),
          },
        ];
      }
      return acc;
    }

    return [
      ...acc,
      {
        title: typeof col.title === "string" ? col.title : "",
        dataIndex: col.dataIndex,
        excelWidth: 20,
        excelValueFormatter:
          col.dataIndex === "datumEvidentiranja"
            ? (val) => formatDateDDMMYY(val as string) ?? ""
            : formatExcelCellValue,
      },
    ];
  }, []);
};
